import { useState } from 'react';
import { FlashcardBack, FlashcardFront } from '../../components/FlashcardFace';
import { FlipCard } from '../../components/FlipCard';
import { SpeakButton } from '../../components/SpeakButton';
import { ttsTextOf } from '../../components/speech';
import { RatingButtons, type SrsRating } from '../../ds';
import { WEB_ICO } from '../../shell/icons';
import { FlashcardSettingsModal, type FlashcardSettings } from './FlashcardSettingsModal';
import { screenShell } from '../lesson/common';
import { RATING_MAP, toButtonIntervals } from '../srs/srsRating';
import type { LearningItemView } from '../../api/content.types';
import type { RatingIntervals, ReviewRating } from '../../api/srs.types';

type Mark = 'known' | 'unknown';
type Phase = 'browsing' | 'summary';

function shuffleArray<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

function orderDeck(items: LearningItemView[], shuffle: boolean): LearningItemView[] {
  return shuffle ? shuffleArray(items) : items;
}

export interface FlashcardDeckViewProps {
  /** Bộ thẻ đã sẵn sàng — cha PHẢI tự lo loading/error/rỗng trước khi render view này. */
  items: LearningItemView[];
  /** Tên hiển thị ở header (tên Bài hoặc tên nhóm chữ). */
  title: string;
  onBack: () => void;
  /** Danh từ cho nút ở màn tổng kết: 'bài' (Minna) hoặc 'nhóm' (thư viện chữ Nhật). */
  deckNoun?: string;
  /** Danh từ cho đơn vị thẻ ở màn tổng kết: 'từ' (Minna) hoặc 'thẻ' (kana/bộ thủ). */
  itemNoun?: string;
  /**
   * Có = chế độ SRS: mỗi lần Chưa thuộc/Đã thuộc được lưu và lên lịch FSRS.
   * Không truyền = duyệt tự do (SRS tắt, hoặc bộ thẻ tĩnh như thư viện chữ Nhật).
   */
  srs?: FlashcardDeckSrs;
}

export interface FlashcardDeckSrs {
  /** Khoảng ôn dự kiến ban đầu của từng item, key = item.id. */
  intervals: Record<string, RatingIntervals>;
  /** Lưu 1 lần rating, trả preview mới của thẻ đó. Reject = chưa lưu — view giữ nguyên thẻ đang hiện. */
  submit: (itemId: string, rating: ReviewRating) => Promise<RatingIntervals>;
}

export function FlashcardDeckView({ items, title, onBack, deckNoun = 'bài', itemNoun = 'từ', srs }: FlashcardDeckViewProps) {
  const [settings, setSettings] = useState<FlashcardSettings>({ startFace: 'front', shuffle: false });
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [activeDeck, setActiveDeck] = useState<LearningItemView[]>(() => orderDeck(items, false));
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [marks, setMarks] = useState<Record<string, Mark>>({});
  const [phase, setPhase] = useState<Phase>('browsing');
  const [submitting, setSubmitting] = useState(false);
  const [intervals, setIntervals] = useState(srs?.intervals ?? {});

  const startNewPass = (newItems: LearningItemView[], shuffle: boolean, startFace: FlashcardSettings['startFace'] = settings.startFace) => {
    setActiveDeck(orderDeck(newItems, shuffle));
    setIndex(0);
    setRevealed(startFace === 'back');
    setMarks({});
    setPhase('browsing');
  };

  const mark = (m: Mark) => {
    const currentItem = activeDeck[index];
    setMarks((prev) => ({ ...prev, [currentItem.id]: m }));
    setRevealed(settings.startFace === 'back');
    if (index < activeDeck.length - 1) setIndex(index + 1);
    else setPhase('summary');
  };

  const rate = async (rating: SrsRating) => {
    if (!srs || submitting) return;
    const currentItem = activeDeck[index];
    setSubmitting(true);
    try {
      const nextIntervals = await srs.submit(currentItem.id, RATING_MAP[rating]);
      setIntervals((prev) => ({ ...prev, [currentItem.id]: nextIntervals }));
      mark(rating === 'chuaThuoc' ? 'unknown' : 'known');
    } catch {
      // Chưa lưu được (cha đã báo lỗi) — ở lại thẻ này để user bấm lại.
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'summary') {
    const knownCount = activeDeck.filter((it) => marks[it.id] === 'known').length;
    const unknownItems = activeDeck.filter((it) => marks[it.id] === 'unknown');

    return (
      <div style={screenShell}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 32 }}>
          <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {WEB_ICO.check('var(--success)', 34)}
          </div>
          <h1 style={{ fontSize: 23, fontWeight: 700, color: 'var(--ink)', textAlign: 'center', margin: 0 }}>Hoàn thành bộ thẻ!</h1>
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--success)' }}>{knownCount}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Đã thuộc</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--error)' }}>{unknownItems.length}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Chưa thuộc</div>
            </div>
          </div>
          {unknownItems.length > 0 && (
            <div style={{ fontSize: 15, color: 'var(--text-secondary)', textAlign: 'center' }}>Bạn có muốn ôn tiếp các từ chưa thuộc không?</div>
          )}
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border-default)' }}>
          {unknownItems.length > 0 && (
            <button
              onClick={() => startNewPass(unknownItems, settings.shuffle)}
              style={{
                height: 52,
                borderRadius: 'var(--radius-control)',
                background: 'var(--cta-default)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-vi)',
                fontWeight: 600,
                fontSize: 16,
              }}
            >
              {`Ôn tiếp ${unknownItems.length} ${itemNoun} chưa thuộc`}
            </button>
          )}
          <button
            onClick={() => startNewPass(items, settings.shuffle)}
            style={{
              height: 52,
              borderRadius: 'var(--radius-control)',
              background: 'var(--surface-tint)',
              border: '1px solid var(--border-default)',
              color: 'var(--ink)',
              cursor: 'pointer',
              fontFamily: 'var(--font-vi)',
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            {`Ôn lại tất cả thẻ của ${deckNoun} này`}
          </button>
          <button
            onClick={onBack}
            style={{ height: 48, background: 'none', border: 'none', color: 'var(--cta-default)', cursor: 'pointer', fontFamily: 'var(--font-vi)', fontWeight: 600, fontSize: 15 }}
          >
            {`Ôn ${deckNoun} khác`}
          </button>
        </div>
      </div>
    );
  }

  const item = activeDeck[index];

  return (
    <div style={screenShell}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px 8px' }}>
        <button
          onClick={onBack}
          aria-label="Quay lại"
          style={{ width: 44, height: 44, marginLeft: -10, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {WEB_ICO.back('var(--ink)', 24)}
        </button>
        <h2 style={{ flex: 1, fontSize: 17, fontWeight: 700, color: 'var(--ink)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h2>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{`${index + 1}/${activeDeck.length}`}</span>
        <button
          onClick={() => setSettingsOpen(true)}
          aria-label="Cài đặt Flashcard"
          style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {WEB_ICO.gear('var(--ink)', 20)}
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', padding: 24, minHeight: 0, overflow: 'hidden' }}>
        <FlipCard
          key={item.id}
          flipped={revealed}
          onClick={() => setRevealed((r) => !r)}
          onSwipe={(direction) => {
            const rating = direction === 'right' ? 'daThuoc' : 'chuaThuoc';
            return srs ? rate(rating) : mark(rating === 'chuaThuoc' ? 'unknown' : 'known');
          }}
          swipeDisabled={submitting}
          front={<FlashcardFront item={item} />}
          back={<FlashcardBack item={item} />}
          overlay={<SpeakButton text={ttsTextOf(item)} variant="plain" size={48} />}
        />
      </div>

      {srs ? (
        <div style={{ padding: 20, borderTop: '1px solid var(--border-default)' }}>
          <RatingButtons
            onRate={rate}
            disabled={submitting}
            intervalMinutes={intervals[item.id] && toButtonIntervals(intervals[item.id])}
            icons={{ daThuoc: WEB_ICO.check('var(--cta-default)', 18) }}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, padding: 20, borderTop: '1px solid var(--border-default)' }}>
          <button
            onClick={() => {
              setRevealed(settings.startFace === 'back');
              setIndex((i) => Math.max(i - 1, 0));
            }}
            disabled={index === 0}
            aria-label="Thẻ trước"
            style={{
              width: 52,
              height: 56,
              borderRadius: 999,
              border: '1.5px solid var(--border-default)',
              background: 'var(--surface-page)',
              cursor: index === 0 ? 'not-allowed' : 'pointer',
              opacity: index === 0 ? 0.4 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {WEB_ICO.back('var(--ink)', 20)}
          </button>
          <RatingButtons
            onRate={(rating) => mark(rating === 'chuaThuoc' ? 'unknown' : 'known')}
            icons={{ daThuoc: WEB_ICO.check('var(--cta-default)', 18) }}
          />
        </div>
      )}

      {settingsOpen && (
        <FlashcardSettingsModal
          settings={settings}
          onClose={() => setSettingsOpen(false)}
          onApply={(s) => {
            setSettings(s);
            setSettingsOpen(false);
            startNewPass(items, s.shuffle, s.startFace);
          }}
          onReset={() => {
            setSettingsOpen(false);
            startNewPass(items, settings.shuffle);
          }}
        />
      )}
    </div>
  );
}
